import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Section from '../components/shared/Section'
import Card from '../components/shared/Card'
import SEO from '../components/shared/SEO'
import { getAllPosts } from '../lib/sanity'
import { formatDate } from '../lib/utils'
import { ROUTE_META } from '../lib/seo'

export default function Blog() {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getAllPosts()
      .then((data) => {
        setPosts(data)
        setLoading(false)
      })
      .catch((error) => {
        console.error('Error fetching posts:', error)
        setLoading(false)
      })
  }, [])

  return (
    <>
      <SEO
        title={ROUTE_META['/blog'].title}
        description={ROUTE_META['/blog'].description}
        jsonLd={ROUTE_META['/blog'].jsonLd}
      />

      {/* Hero Section */}
      <Section>
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl text-neon-yellow mb-6">
            Thoughts on design, strategy, and delivery
          </h1>
          <p className="text-xl text-neutral-400">
            Insights from building products and services that deliver real value
          </p>
        </div>
      </Section>

      {/* Blog Listing Section */}
      <Section>
        {loading ? (
          <div className="text-center text-neutral-400">
            Loading posts...
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center text-neutral-400 max-w-2xl mx-auto">
            <p className="text-xl mb-4">
              No blog posts yet. Check back soon for insights on service design, product strategy, and human-centered delivery.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <Link key={post._id} to={`/blog/${post.slug.current}`}>
                <Card>
                  {post.mainImage && (
                    <div className="mb-4 -mx-6 -mt-6 rounded-t-xl overflow-hidden">
                      <img
                        src={post.mainImage}
                        alt={post.title}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-neutral-500 mb-3">
                    <span>{formatDate(post.publishedAt)}</span>
                    {post.readTime && (
                      <>
                        <span>•</span>
                        <span>{post.readTime} min read</span>
                      </>
                    )}
                  </div>
                  <h3 className="text-2xl text-neutral-100 mb-3 hover:text-neon-lime transition-colors">
                    {post.title}
                  </h3>
                  {post.excerpt && (
                    <p className="text-neutral-400 mb-4">
                      {post.excerpt}
                    </p>
                  )}
                  {post.categories && post.categories.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {post.categories.map((category, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-neon-pink/10 text-neon-pink rounded-full text-sm border border-neon-pink/30"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  )}
                </Card>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </>
  )
}
